import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SchoolIcon from '@mui/icons-material/School';
import {
  OnboardingContentSettings,
  SupportContentAudience,
} from '../../services/onboardingService';

interface WelcomeModalProps {
  open: boolean;
  onStartTour: () => void;
  onSkip: () => void;
  userName?: string;
  viewerAudience: SupportContentAudience;
  content: OnboardingContentSettings['welcomeModal'];
}

const featureIcons = ['🔍', '⬇️', '📁', '✨', '⚙️', '📚'];

const canViewAudience = (
  itemAudience: SupportContentAudience,
  viewerAudience: SupportContentAudience
) => {
  if (itemAudience === 'all') return true;
  if (itemAudience === 'editor') {
    return viewerAudience === 'editor' || viewerAudience === 'admin';
  }
  return viewerAudience === 'admin';
};

const WelcomeModal: React.FC<WelcomeModalProps> = ({
  open,
  onStartTour,
  onSkip,
  userName,
  viewerAudience,
  content,
}) => {
  const handleStartTour = () => {
    onStartTour();
  };

  const handleSkip = () => {
    onSkip();
  };

  const visibleSections = content.sections.filter((section) =>
    canViewAudience(section.audience, viewerAudience)
  );
  const visibleFeatures = content.features.filter((feature) =>
    canViewAudience(feature.audience, viewerAudience)
  );
  const title = content.appendUserName && userName
    ? (content.title ? `${content.title}, ${userName}!` : userName)
    : content.title;

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          background: 'linear-gradient(180deg, #1B3967 0%, #0D1F3C 100%)',
          color: 'white'
        }
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={handleSkip}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: 'rgba(255, 255, 255, 0.7)',
          '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ textAlign: 'center', py: 5, px: 4 }}>
        {/* Icon */}
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3
          }}
        >
          <RocketLaunchIcon sx={{ fontSize: 40, color: '#FFD700' }} />
        </Box>

        {/* Title */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: 2,
            fontFamily: '"Oswald", sans-serif',
            textTransform: 'uppercase',
            letterSpacing: 1
          }}
        >
          {title}
        </Typography>

        {/* Subtitle */}
        {content.subtitle && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 400,
              mb: 2,
              opacity: 0.9
            }}
          >
            {content.subtitle}
          </Typography>
        )}

        {/* Description */}
        {visibleSections.length > 0 && (
          <Box sx={{ mb: 4, maxWidth: 460, mx: 'auto' }}>
            {visibleSections.map((section, index) => (
              <Typography
                key={section.id}
                sx={{
                  fontSize: '1rem',
                  lineHeight: 1.7,
                  opacity: 0.85,
                  mb: index === visibleSections.length - 1 ? 0 : 2,
                  whiteSpace: 'pre-line'
                }}
              >
                {section.text}
              </Typography>
            ))}
          </Box>
        )}

        {/* Features preview */}
        {visibleFeatures.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 4,
              mb: 4,
              flexWrap: 'wrap'
            }}
          >
            {visibleFeatures.map((feature, index) => (
              <Box
                key={feature.id}
                sx={{
                  textAlign: 'center',
                  opacity: 0.8
                }}
              >
                <Typography sx={{ fontSize: '1.5rem', mb: 0.5 }}>
                  {featureIcons[index % featureIcons.length]}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                  {feature.label}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Buttons */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleStartTour}
            startIcon={<SchoolIcon />}
            sx={{
              bgcolor: 'white',
              color: '#1B3967',
              fontWeight: 600,
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              '&:hover': {
                bgcolor: '#f5f5f5'
              }
            }}
          >
            {content.primaryButtonLabel}
          </Button>

          <Button
            variant="text"
            onClick={handleSkip}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              textTransform: 'none',
              '&:hover': {
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            {content.secondaryButtonLabel}
          </Button>
        </Box>

        {/* Footer note */}
        {content.footerNote && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 4,
              opacity: 0.5,
              fontSize: '0.7rem',
              whiteSpace: 'pre-line'
            }}
          >
            {content.footerNote}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeModal;

